<template>
  <PriorHeader />
  <v-container class="all-patients-container p-0" :fluid="true">
    <v-alert
      v-if="errorMsg"
      :text="errorMsg"
      type="error"
      variant="outlined"
      closable
      @click:close="errorMsg = ''"></v-alert>
    <div class="all-patients-list-container">
      <div class="all-patients-list">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-primary weight-600">All Patients</h1>
          <v-btn color="primary" prepend-icon="mdi-upload" @click="onOpenFileUpload"> Upload Intake Doc </v-btn>
        </div>
        <v-skeleton-loader v-if="preloader" type="list-item, table" />
        <v-container v-if="!preloader" fluid class="px-0">
          <v-row>
            <v-col lg="2" sm="4">
              <v-select
                v-model="selectedStatus"
                :items="VALID_INTAKE_LIST_STATUSES"
                label="Filter by Status"
                variant="solo"
                @update:model-value="fetchAndProcessLanes"></v-select>
            </v-col>
            <v-col cols="2" class="d-flex align-center">
              <text-date-input
                :model-value="dateFilter"
                :max-date="new Date()"
                label="Filter by Date"
                @update:model-value="onUpdateDateFilter" />
            </v-col>
            <v-col cols="3" md="4">
              <v-text-field
                v-model="patientSearchQuery"
                type="text"
                prepend-inner-icon="mdi-magnify"
                placeholder="Patient Name"
                @keyup.enter="searchPatient" />
            </v-col>
            <v-col cols="2" class="d-flex align-center ga-2">
              <v-btn color="primary" @click="searchPatient">Search</v-btn>
              <v-btn
                v-if="hasActiveFilters()"
                icon="mdi-close"
                variant="text"
                size="small"
                title="Clear filters"
                @click="onClearFilters"></v-btn>
            </v-col>
          </v-row>
        </v-container>
        <div v-if="Object.keys(documentsByLane).length > 0" class="patients-kanban">
          <KanbanLanes
            id="lane1"
            :key="kanbanKey"
            :lane-cards="documentsByLane"
            :lanes="lanes"
            @on-header-menu="handleHeaderMenu"
            @next-page="fetchNextPage" />
        </div>
      </div>
    </div>
  </v-container>

  <v-dialog v-model="demoFileInput" max-width="500">
    <v-card>
      <v-card-title class="text-h5 pa-4">
        <template v-if="!uploadComplete"> Upload PDF Documents for Intake </template>
        <template v-else> Upload Complete </template>
      </v-card-title>
      <v-card-text class="pa-4">
        <v-alert
          v-if="errorMsgDialog"
          :text="errorMsgDialog"
          type="error"
          variant="outlined"
          closable
          @click:close="errorMsgDialog = ''"></v-alert>
        <template v-if="!uploadComplete">
          <v-file-upload
            v-model="selectedFiles"
            accept=".pdf"
            label="Select files"
            prepend-icon="mdi-paperclip"
            :disabled="demoInputDisabled || isUploading"
            :show-progress="true"
            multiple
            clearable
            @update:model-value="handleFileChange"></v-file-upload>
        </template>
        <v-list v-if="selectedFiles.length > 0" class="mt-4 rounded-16">
          <v-list-item
            v-for="(file, index) in selectedFiles"
            :key="index"
            :title="file.name"
            :subtitle="formatFileSize(file.size)">
            <template #append>
              <div class="d-flex align-center">
                <v-progress-circular
                  v-if="uploadProgress[index] !== undefined && uploadProgress[index] < 100"
                  :model-value="uploadProgress[index]"
                  color="primary"
                  size="24"
                  width="2"
                  class="mr-2"></v-progress-circular>
                <v-icon
                  v-else-if="uploadProgress[index] === 100"
                  color="success"
                  icon="mdi-check-circle"
                  class="mr-2"></v-icon>
                <v-btn
                  v-if="!isUploading && !uploadComplete"
                  icon="mdi-close"
                  variant="text"
                  @click="removeFile(index)"></v-btn>
              </div>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <template v-if="!uploadComplete">
          <v-btn color="primary" variant="text" :disabled="isUploading" @click="demoFileInput = false"> Cancel </v-btn>
          <v-btn
            color="primary"
            :disabled="selectedFiles.length === 0 || demoInputDisabled || isUploading"
            @click="handleUpload">
            Upload
          </v-btn>
        </template>
        <template v-else>
          <v-btn color="primary" @click="handleOk"> OK </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="archiveReasonDialog" max-width="800">
    <ArchiveNote
      :order-info="archiveNoteProps.archiveOrderInfo"
      :intake-order="archiveNoteProps.archiveOrder"
      :intake-document="archiveNoteProps.archiveDocument"
      @on-archived="onArchived" />
  </v-dialog>
  <PriorFooter />
</template>

<script setup>
import { ref, onMounted } from "vue";
import { uuid } from "vue-uuid";
import { useDate } from "vuetify";
import PriorHeader from "@/components/PriorHeader";
import PriorFooter from "@/components/PriorFooter";
import KanbanLanes from "@/components/kanban/KanbanLanes";
import { intakeService } from "@/services/intakeService";
import { uploadService } from "@/services/uploadService";
import router from "@/router";
import { orderInfoService } from "@/services/orderInfoService";
import { IntakeOrder, IntakeDocument } from "@/models/intake";
import executor from "@/services/api";
import { ABBR_MONTHS } from "@/utils/constants";
import { useIntakeActions } from "@/composables/useIntakeActions";
import { useIntakeListFilters } from "@/composables/useIntakeListFilters";
import { useAuthStore } from "@/stores/authStore";
import { timeAgoString } from "@/utils";
import ArchiveNote from "@/components/ArchiveNote.vue";
import { mainServices } from "@/services/mainServices";
import { INTAKE_LIST_STATUS, VALID_INTAKE_LIST_STATUSES } from "@/constants/intakeFilters";

const demoFileInput = ref(false);
const demoInputDisabled = ref(false);
const selectedFiles = ref([]);
const isUploading = ref(false);
const uploadProgress = ref({});
const uploadComplete = ref(false);
const dateAdapter = useDate();
const { selectedStatus, patientSearchQuery, dateFilter, sortBy, updateUrlFromFilters, clearFilters, hasActiveFilters } =
  useIntakeListFilters();
const intakeActions = useIntakeActions();
const clientConfig = ref(null);

const archiveReasonDialog = ref(false);
const archiveNoteProps = ref({
  archiveDocument: null,
  archiveOrder: null,
  archiveOrderInfo: null,
});

const documentStatusFilterMap = {
  [INTAKE_LIST_STATUS.ACTIVE]: "PRE_PROCESSING,OCR_PROCESSED,INTAKE,EHR_PROCESSING,OCR_FAILED",
  [INTAKE_LIST_STATUS.ARCHIVED]: "ARCHIVED",
  [INTAKE_LIST_STATUS.COMPLETED]: "INTAKE_COMPLETE,SENT_TO_EHR",
};

const orderInfoStatusFilterMap = {
  [INTAKE_LIST_STATUS.ACTIVE]: "Created,Processing,Sent to plan",
  [INTAKE_LIST_STATUS.ARCHIVED]: "Archived",
  [INTAKE_LIST_STATUS.COMPLETED]:
    "Approved,Denied,Payor/Medication not supported,Response received,No payor response received,Requires more information,Prior authorization not required",
};

const intakeHeaderMenu = [
  {
    text: "Newest",
    value: "sort_newest",
  },
  {
    text: "Oldest",
    value: "sort_oldest",
  },
];

const onOpenFileUpload = () => {
  errorMsgDialog.value = "";
  demoFileInput.value = true;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const handleFileChange = (files) => {
  selectedFiles.value = files.filter((file) => file.type === "application/pdf");
  if (selectedFiles.value.length !== files.length) {
    errorMsgDialog.value = "Only PDF files are supported, some files were removed from your selection.";
  }
  // Reset progress when files change
  uploadProgress.value = {};
};

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
  delete uploadProgress.value[index];
};

const handleUpload = async () => {
  if (selectedFiles.value.length === 0) return;
  isUploading.value = true;
  demoInputDisabled.value = true;

  try {
    for (let i = 0; i < selectedFiles.value.length; i++) {
      const file = selectedFiles.value[i];
      uploadProgress.value[i] = 0;

      // Simulate progress (replace with actual upload progress if available)
      const progressInterval = setInterval(() => {
        if (uploadProgress.value[i] < 90) {
          uploadProgress.value[i] += 10;
        }
      }, 200);

      try {
        const uuidv4 = uuid.v4();
        let uploadKey = uuidv4;
        const fileName = file.name;
        uploadKey = uuidv4 + "/" + fileName;
        const downloadLink = await uploadService.uploadFile(file, uploadKey);
        await intakeService.postDemoUpload({
          link: downloadLink,
        });
        uploadProgress.value[i] = 100;
      } catch (error) {
        uploadProgress.value[i] = -1; // Indicate error
      } finally {
        clearInterval(progressInterval);
      }
    }

    // Show success state
    uploadComplete.value = true;
    // Refresh the patient list
    preloader.value = true;
    await fetchAndProcessLanes();
  } catch (error) {
  } finally {
    isUploading.value = false;
    demoInputDisabled.value = false;
  }
};

const handleOk = async () => {
  // Reset and close dialog
  uploadComplete.value = false;
  selectedFiles.value = [];
  uploadProgress.value = {};
  demoFileInput.value = false;
};

const onArchived = () => {
  archiveReasonDialog.value = false;
  fetchAndProcessLanes();
};

const preloader = ref(true);
const kanbanKey = ref(0);
const documentsByLane = ref({});
const lanes = ref([]);
const errorMsg = ref("");
const authStore = useAuthStore();
const errorMsgDialog = ref("");

const statusToTitle = {
  INTAKE: "INBOX",
  QUALIFY_PATIENT: "SELECT INSURANCE",
  CLINICAL_REVIEW: "DOCUMENT REVIEW",
  PRIOR_AUTHORIZATION: "ORDERS",
  ARCHIVED: "ORDERS",
};

const SubmissionStatusToTitle = {
  Processing: "PRIOR AUTH",
  PriorAuthActionNeeded: "ACTION NEEDED",
  ResponseActionNeeded: "REVIEW RESPONSE",
};

onMounted(async () => {
  // We keep client config local to the page to avoid the LH company config from
  // changing the client document page configs
  clientConfig.value = await mainServices.getCurrentUserClientConfig();
  fetchAndProcessLanes();
});

const getLaneDetail = (title, { sortBy = "newest" } = {}) => {
  switch (title) {
    case statusToTitle.INTAKE:
      return {
        title: statusToTitle.INTAKE,
        process: processIntakeDocuments,
        headerMenu: intakeHeaderMenu,
        fetch: [getAllIntakeDocuments, sortBy],
      };
    case statusToTitle.QUALIFY_PATIENT:
      return {
        title: statusToTitle.QUALIFY_PATIENT,
        process: (response) => processOrders(response, "QUALIFY_PATIENT"),
        fetch: [fetchOrders, "QUALIFY_PATIENT"],
      };
    case statusToTitle.CLINICAL_REVIEW:
      return {
        title: statusToTitle.CLINICAL_REVIEW,
        process: (response) => processOrders(response, "CLINICAL_REVIEW"),
        fetch: [fetchOrders, "CLINICAL_REVIEW"],
      };
    case statusToTitle.PRIOR_AUTHORIZATION:
      return {
        title: statusToTitle.PRIOR_AUTHORIZATION,
        process: (response) => processOrders(response, "PRIOR_AUTHORIZATION"),
        fetch: [fetchOrders, "PRIOR_AUTHORIZATION"],
      };
    case "orders:Archived":
      return {
        title: statusToTitle.ARCHIVED,
        process: (response) => processOrders(response, "ARCHIVED"),
        fetch: [fetchOrders, "ARCHIVED"],
      };
    case "Submissions:Archived":
      return {
        title: SubmissionStatusToTitle.Processing,
        process: (response) => processSubmissions(response, "Processing"),
        fetch: [fetchSubmissions, "Archived"],
      };
    case "Submissions:PriorAuthActionNeeded":
      return {
        title: SubmissionStatusToTitle.PriorAuthActionNeeded,
        process: (response) => processSubmissions(response, "PriorAuthActionNeeded"),
        fetch: [
          fetchSubmissions,
          "Initiated,Requires review,Review demographics info,Review clinical info,Incomplete,Sent to prescriber",
        ],
      };
    case "Submissions:ResponseActionNeeded":
      return {
        title: SubmissionStatusToTitle.ResponseActionNeeded,
        process: (response) => processSubmissions(response, "ResponseActionNeeded"),
        fetch: [
          fetchSubmissions,
          "Approved,Denied,Payor/Medication not supported,Response received,No payor response received,Requires more information,Prior authorization not required",
        ],
      };
    case "Submissions:Processing":
      return {
        title: SubmissionStatusToTitle.Processing,
        process: (response) => processSubmissions(response, "Processing"),
        fetch: [fetchSubmissions, orderInfoStatusFilterMap[selectedStatus.value]],
      };
    default:
      throw new Error(`Invalid lane title: ${title}`);
  }
};

const fetchAndProcessLanes = async () => {
  // Clear existing data
  documentsByLane.value = {};
  lanes.value = [];

  preloader.value = true;

  let laneDetails = [];
  let promises = [];

  if (selectedStatus.value === INTAKE_LIST_STATUS.ARCHIVED) {
    laneDetails = [
      getLaneDetail(statusToTitle.INTAKE),
      getLaneDetail("orders:Archived"),
      getLaneDetail("Submissions:Archived"),
    ];
  } else if (selectedStatus.value === INTAKE_LIST_STATUS.COMPLETED) {
    laneDetails = [
      getLaneDetail(statusToTitle.INTAKE),
      getLaneDetail(statusToTitle.PRIOR_AUTHORIZATION),
      getLaneDetail("Submissions:Processing"),
    ];
  } else {
    const defaultSortBy = clientConfig.value?.uiConfig?.intake?.kanbanSettings?.inboxSortBy;
    const inboxSortBy = sortBy.value || defaultSortBy;
    laneDetails = [
      getLaneDetail(statusToTitle.INTAKE, {
        sortBy: inboxSortBy,
      }),
      getLaneDetail(statusToTitle.QUALIFY_PATIENT),
      getLaneDetail(statusToTitle.CLINICAL_REVIEW),
      getLaneDetail("Submissions:Processing"),
      getLaneDetail("Submissions:PriorAuthActionNeeded"),
      getLaneDetail("Submissions:ResponseActionNeeded"),
    ];
  }

  promises = laneDetails.map((lane) => {
    return lane.fetch[0](...lane.fetch.slice(1));
  });
  const results = await Promise.allSettled(promises);

  for (let i = 0; i < laneDetails.length; i++) {
    const lane = laneDetails[i];
    if (results[i].status === "fulfilled") {
      lanes.value.push({ title: lane.title, count: results[i].value.count, headerMenu: lane.headerMenu });
      lane.process(results[i].value);
    }
  }

  preloader.value = false;
};

const refreshLane = async (title, laneOptions = {}) => {
  const lane = lanes.value.find((lane) => lane.title === title);
  if (!lane) {
    return;
  }
  documentsByLane.value[title] = [];
  const laneDetail = getLaneDetail(title, laneOptions);
  const response = await laneDetail.fetch[0](...laneDetail.fetch.slice(1));
  lane.count = response.count;
  laneDetail.process(response);
};

const fetchNextPage = async (title) => {
  const lane = lanes.value.find((lane) => lane.title === title);
  const response = await executor.get(lane.nextPage[0]);
  // lane.nextPage = response.next;
  lane.nextPage[1](response);
};

const getAllIntakeDocuments = async (sortBy = "newest") => {
  // Fetching data from API
  try {
    const response = await intakeService.getIntakeDocuments({
      status: documentStatusFilterMap[selectedStatus.value],
      sortBy: sortBy,
      patient: patientSearchQuery.value.trim(),
      date: dateFilter.value,
    });
    return response;
  } catch (ex) {
    errorMsg.value = "Failed to fetch documents. Please try again later.";
  }
  kanbanKey.value++;
};

const formatSnoozeUntil = (snoozeUntil) => {
  const nowDate = new Date();
  if (snoozeUntil.toDateString() === nowDate.toDateString()) {
    return `Snoozed Until ${snoozeUntil.getHours()}:${snoozeUntil.getMinutes()}`;
  }
  return `Snoozed Until ${ABBR_MONTHS[snoozeUntil.getMonth()]} ${snoozeUntil.getDate()}`;
};

const snoozeIntakeDocument = async (doc, minutes) => {
  preloader.value = true;
  try {
    await intakeActions.onSnooze({ documentKey: doc.documentKey, minutes });
    await fetchAndProcessLanes();
  } finally {
    preloader.value = false;
  }
};

const snoozeIntakeOrder = async (order, minutes) => {
  preloader.value = true;
  try {
    await intakeActions.onSnooze({ orderId: order.id, minutes });
    await fetchAndProcessLanes();
  } finally {
    preloader.value = false;
  }
};

const getSnoozeMenuItems = (item, snoozeFn) => {
  const items = [
    {
      text: "Snooze for 15 minutes",
      onClick: async () => {
        await snoozeFn(item, 15);
      },
    },
    {
      text: "Snooze for 1 day",
      onClick: async () => {
        await snoozeFn(item, 24 * 60);
      },
    },
    {
      text: "Snooze for 1 week",
      onClick: async () => {
        await snoozeFn(item, 24 * 7 * 60);
      },
    },
  ];
  if (item.isSnoozed) {
    items.unshift({
      text: "Remove snooze",
      onClick: async () => {
        await snoozeFn(item, 0);
      },
    });
  }

  // Add archive option
  if (item.status !== "ARCHIVED") {
    items.push({
      text: "Archive",
      onClick: () => {
        archiveReasonDialog.value = true;
        archiveNoteProps.value.archiveOrderInfo = null;
        archiveNoteProps.value.archiveOrder = item.documentKey ? null : item;
        archiveNoteProps.value.archiveDocument = item.documentKey ? item : null;
      },
    });
  } else {
    items.push({
      text: "Unarchive",
      onClick: async () => {
        if (confirm("Are you sure you want to unarchive this item?")) {
          if (item.documentKey) {
            // This is a document
            await intakeService.deleteArchiveDocument(item.documentKey);
          } else {
            // This is an intake order
            await intakeService.deleteArchiveOrder(item.id);
          }
          await fetchAndProcessLanes();
        }
      },
    });
  }
  return items;
};

const processIntakeDocuments = (response) => {
  const mappedResponse = response.results.map((r) => {
    const doc = new IntakeDocument(r, dateAdapter);
    const processed = ["INTAKE", "INTAKE_COMPLETE", "SENT_TO_EHR", "ARCHIVED"].includes(doc.status);
    const patientDetails = doc.patient ?? doc.patientGuess;
    let disabled = !processed;
    const readOnlyDocument = doc.status !== "INTAKE";

    const statusMap = {
      PRE_PROCESSING: "In Process",
      OCR_PROCESSED: "In Process",
      OCR_FAILED: "In Process",
      INTAKE: "Ready for Review",
      INTAKE_COMPLETE: "Reviewed",
      ARCHIVED: "Archived",
      EHR_PROCESSING: "In Process",
      SENT_TO_EHR: "Sent to EHR",
    };

    const statusColorMap = {
      PRE_PROCESSING: "error",
      OCR_PROCESSED: "error",
      OCR_FAILED: "error",
      INTAKE: "success",
      INTAKE_COMPLETE: "success",
      ARCHIVED: "blue-gray",
      EHR_PROCESSING: "error",
      SENT_TO_EHR: "success",
    };

    let tagText = statusMap[doc.status] ?? "In Process";
    let tagColor = statusColorMap[doc.status] ?? "error";
    let tagTooltip = null;
    if (doc.isSnoozed) {
      tagText = formatSnoozeUntil(doc.snoozeUntil);
      tagColor = "warning";
    }
    if (doc.status === "ARCHIVED" && doc.archivedReason) {
      tagTooltip = doc.archivedReason;
    }

    if (doc.lock && doc.lock.userPk !== authStore.userInfo.pk) {
      disabled = true;
      tagColor = "gray";
      tagText = `Locked`;
      tagTooltip = `${doc.lock.user.firstName} ${doc.lock.user.lastName} is reviewing this document.`;
    }

    return {
      title: `${patientDetails?.firstName ?? ""} ${patientDetails?.lastName ?? ""}`.trim() || "Unknown Patient",
      disabled: disabled,
      desc: ["Received at", `${doc.createdOn.toLocaleString()}`, `In stage for ${timeAgoString(doc.statusChangedAt)}`],
      tagColor: tagColor,
      tagText: tagText,
      status: doc.status,
      tagTooltip: tagTooltip,
      documentKey: doc.documentKey,
      menuItems: getSnoozeMenuItems(doc, snoozeIntakeDocument),
      onClick: () => {
        router.push(`intake-review/${doc.documentKey}?${readOnlyDocument ? "ro=1" : ""}`);
      },
    };
  });

  // Append to the existing list
  const current = documentsByLane.value[statusToTitle.INTAKE] ?? [];
  documentsByLane.value[statusToTitle.INTAKE] = [...current, ...mappedResponse];

  // Set the next page
  const lane = lanes.value.find((lane) => lane.title === statusToTitle.INTAKE);
  if (response.next) {
    lane.nextPage = [response.next, processIntakeDocuments];
  } else {
    lane.nextPage = null;
  }
};

const getOrderPageUrl = (order) => {
  if (order.status === "CLINICAL_REVIEW") {
    return `/intake/order/${order.id}/document-review`;
  } else if (order.status === "QUALIFY_PATIENT") {
    return `/intake/order/${order.id}/qualify`;
  } else if (order.status === "PRIOR_AUTHORIZATION") {
    return `check-my-coverage/${order.orderInfo}`;
  } else if (order.status === "ARCHIVED") {
    return `/intake/order/${order.id}/qualify?ro=1`;
  }
  return "";
};

const fetchOrders = async (status) => {
  try {
    // Fetching data from API
    const response = await intakeService.getOrders({
      status,
      patient: patientSearchQuery.value.trim(),
      date: dateFilter.value,
    });
    return response;
  } catch (e) {
    errorMsg.value = "There was an error fetching the orders";
  }
  kanbanKey.value++;
};

const processOrders = (response, status) => {
  const mappedResponse = response.results.map((r) => {
    const order = new IntakeOrder(r, dateAdapter);
    let processed = true;
    let statusStr = null;
    let customTagColor = null;
    let disabled = false;
    if (order.status === "CLINICAL_REVIEW") {
      let policyReviewExecuted = false;
      if (
        clientConfig.value?.uiConfig?.intake?.policyReviewEnabled &&
        order.policyReview &&
        order.policyReview.status === "completed"
      ) {
        policyReviewExecuted = true;
      } else if (order.policyChecklist.length > 0) {
        policyReviewExecuted = true;
      }
      processed =
        policyReviewExecuted ||
        (order.statusChangedAt && new Date().getTime() - new Date(order.statusChangedAt).getTime() > 3 * 60 * 1000);
      disabled = !processed;
    } else if (order.status === "QUALIFY_PATIENT") {
      processed = !!order.patient?.hasInsuranceCard;
      disabled = !processed;
      statusStr = "Missing Insurance Data";
      if (!processed) {
        customTagColor = "warning";
      }
    } else if (order.status === "ARCHIVED") {
      disabled = false;
      processed = false;
      statusStr = "Archived";
      customTagColor = "gray";
    } else if (order.status === "PRIOR_AUTHORIZATION") {
      processed = false;
      disabled = true;
      statusStr = "Prior Authorization";
      customTagColor = "success";
    }

    let tagText = processed ? "Ready for Review" : statusStr ?? "In Process";
    let tagColor = processed ? "success" : customTagColor ?? "error";
    const tagTooltip = order.status === "ARCHIVED" && order.archivedReason ? order.archivedReason : null;

    if (order.isSnoozed) {
      tagText = formatSnoozeUntil(order.snoozeUntil);
      tagColor = "warning";
    }
    return {
      title: `${order.patient?.firstName} ${order.patient?.lastName}`,
      disabled: disabled,
      desc: [
        "Received at",
        `${order.orderFormDocumentCreatedOn?.toLocaleString()}`,
        `In stage for ${timeAgoString(order.statusChangedAt ?? order.createdOn)}`,
      ],
      tagColor: tagColor,
      tagText: tagText,
      status: order.status,
      tagTooltip: tagTooltip,
      documentKey: order.id,
      allowEdit: order.status === "QUALIFY_PATIENT" && !order.patient?.hasInsuranceCard,
      menuItems: getSnoozeMenuItems(order, snoozeIntakeOrder),
      onEdit: () => {
        router.push({ name: "intake-review", params: { id: order.orderForm?.intakeDocumentKey } });
      },
      onClick: () => {
        router.push(getOrderPageUrl(r));
      },
    };
  });

  // Append to the existing list
  const current = documentsByLane.value[statusToTitle[status]] ?? [];
  documentsByLane.value[statusToTitle[status]] = [...current, ...mappedResponse];

  // Set the next page
  const lane = lanes.value.find((lane) => lane.title === statusToTitle[status]);
  if (response.next) {
    lane.nextPage = [response.next, (response) => processOrders(response, status)];
  } else {
    lane.nextPage = null;
  }
};

const fetchSubmissions = async (status) => {
  try {
    const response = await orderInfoService.getListOfOrders(
      1,
      status,
      patientSearchQuery.value.trim(),
      dateFilter.value
    );
    kanbanKey.value++;
    return response;
  } catch (e) {
    errorMsg.value = "There was an error fetching the submissions";
    throw e;
  }
};

const processSubmissions = (response, status) => {
  const getRoute = (orderStatus) => {
    return orderStatus === "Denied"
      ? "denial-follow-up-order"
      : orderStatus === "Initiated"
      ? "create-submission"
      : "check-my-coverage";
  };
  const colorMap = {
    Created: "blue-gray",
    Incomplete: "warning",
    Initiated: "warning",
    Processing: "blue-gray",
    "Sent to prescriber": "blue-gray",
    "Sent to plan": "blue-gray",
    "Sent to plan by prescriber": "blue-gray",
    "Prior authorization not required": "green",
    "Requires review": "blue",
    "Review demographics info": "blue",
    "Review clinical info": "blue",
    "Payor/Medication not supported": "blue",
    "No payor response received": "blue",
    "Requires more information": "blue",
    Denied: "blue",
    Archived: "blue-gray",
    Expired: "blue-gray",
  };
  const mappedResponse = response.results.map((r) => {
    const menuItems = [];
    if (r.status !== "Archived") {
      menuItems.push({
        text: "Archive",
        onClick: async () => {
          archiveReasonDialog.value = true;
          archiveNoteProps.value.archiveOrderInfo = r;
          archiveNoteProps.value.archiveOrder = null;
          archiveNoteProps.value.archiveDocument = null;
        },
      });
    } else {
      menuItems.push({
        text: "Unarchive",
        onClick: async () => {
          if (confirm("Are you sure you want to unarchive this submission?")) {
            await orderInfoService.unarchiveOrder(r.orderKey);
            await fetchAndProcessLanes();
          }
        },
      });
    }
    const statusTextMap = {
      Created: "Fetching questions",
      "Payor/Medication not supported": "Not supported",
    };

    const tagTooltip = r.status === "Archived" && r.archivedReason ? r.archivedReason : null;

    return {
      title: `${r.patientFirstName ?? ""} ${r.patientLastName ?? ""}`.trim() || "Unknown Patient",
      desc: [
        `${r.medication ?? "Medication not set"}`,
        `In stage for ${timeAgoString(new Date(r.statusModifiedOn ?? r.createdOn))}`,
      ],
      disabled: r.status === "Created" || r.status === "Processing",
      tagColor: colorMap[r.status] ?? "success",
      tagText: statusTextMap[r.status] ?? r.status,
      tagTooltip: tagTooltip,
      orderKey: r.orderKey,
      menuItems: menuItems,
      onClick: () => {
        router.push({ name: getRoute(r.status), params: { id: r.orderKey } });
      },
    };
  });

  // Append to the existing list
  const current = documentsByLane.value[SubmissionStatusToTitle[status]] ?? [];
  documentsByLane.value[SubmissionStatusToTitle[status]] = [...current, ...mappedResponse];

  // Set the next page
  const lane = lanes.value.find((lane) => lane.title === SubmissionStatusToTitle[status]);
  if (response.next) {
    lane.nextPage = [response.next, (response) => processSubmissions(response, status)];
  } else {
    lane.nextPage = null;
  }
};

const searchPatient = async () => {
  updateUrlFromFilters();
  await fetchAndProcessLanes();
};

const onClearFilters = async () => {
  clearFilters();
  await fetchAndProcessLanes();
};

const onUpdateDateFilter = async (value) => {
  dateFilter.value = value;
  await fetchAndProcessLanes();
};

const handleHeaderMenu = async (item, laneTitle) => {
  if (laneTitle === statusToTitle.INTAKE) {
    if (item.value === "sort_newest") {
      sortBy.value = "newest";
      updateUrlFromFilters();
      await refreshLane(statusToTitle.INTAKE, { sortBy: "newest" });
    } else if (item.value === "sort_oldest") {
      sortBy.value = "oldest";
      updateUrlFromFilters();
      await refreshLane(statusToTitle.INTAKE, { sortBy: "oldest" });
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/pages/_patients-list.scss";
</style>
